/**
 * Max Wealth Strategy — faithful port of MT4 Max_Wealth1.mq4
 *
 * Uses DUAL SuperTrend system (Swing.mq4 algorithm run twice):
 *   - ST1 & ST2 with configurable period/multiplier per preset
 *   - Signal fires when BOTH SuperTrends agree on direction
 *     AND it differs from previous combined direction
 *
 * Presets:
 *   Wealth → ST1(10, 3.0) + ST2(8, 2.0)
 *   Forex  → ST1(12, 3.0) + ST2(12, 4.0)
 *
 * SL/TP (ATR-based from MT4):
 *   risk = Entry − SuperTrend_band (distance from entry to ST1 band)
 *   SL   = Entry − direction × risk × 0.682
 *   TP1  = Entry + direction × risk × 0.682
 *   TP2  = Entry + direction × risk × 1.364
 *   TP3  = Entry + direction × risk × 2.046
 *
 * Trailing Stop: Percentage-based (0.35%)
 *   LONG:  TSL = max(prev TSL, price × 99.65 / 100)
 *   SHORT: TSL = min(prev TSL, price × 100.35 / 100)
 *
 * MT4 features ported:
 *   - Dual SuperTrend confirmation
 *   - Buy/Sell arrow markers
 *   - Custom colored candles: Green (#008000), Red (#FF0000), WhiteSmoke (#F5F5F5)
 *   - 3 targets (TP1, TP2, TP3)
 *   - Percentage-based trailing stop
 *   - Target/SL hit detection with markers
 *   - Dashboard metadata for overlay component
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants ───────────────────────────────────────────────────
const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

const SL_PERCENT = 0.682;
const TP1_PERCENT = 0.682;
const TP2_PERCENT = 1.364;
const TP3_PERCENT = 2.046;
const TSL_PERCENT = 0.35;

// ─── Presets ─────────────────────────────────────────────────────
interface Preset {
  st1Period: number; st1Mult: number;
  st2Period: number; st2Mult: number;
}
const PRESETS: Record<string, Preset> = {
  wealth: { st1Period: 10, st1Mult: 3.0, st2Period: 8, st2Mult: 2.0 },
  forex:  { st1Period: 12, st1Mult: 3.0, st2Period: 12, st2Mult: 4.0 },
};

// ─── ATR computation (Wilder smoothing) ──────────────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high, l = candles[i].low, pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── SuperTrend computation (Swing.mq4 algorithm) ────────────────
function computeSuperTrend(
  candles: CandleData[], period: number, multiplier: number,
): { direction: Direction[]; trendUp: number[]; trendDown: number[] } {
  const len = candles.length;
  const direction: Direction[] = new Array(len).fill(DIR_LONG);
  const trendUp: number[] = new Array(len).fill(0);
  const trendDown: number[] = new Array(len).fill(0);

  if (len < period + 2) return { direction, trendUp, trendDown };

  const atr = computeATR(candles, period);
  const upperRaw: number[] = new Array(len).fill(0);
  const lowerRaw: number[] = new Array(len).fill(0);
  const upper: number[] = new Array(len).fill(0);
  const lower: number[] = new Array(len).fill(0);

  for (let i = 0; i < len; i++) {
    const mid = (candles[i].high + candles[i].low) / 2.0;
    upperRaw[i] = mid + multiplier * atr[i];
    lowerRaw[i] = mid - multiplier * atr[i];
  }

  upper[0] = upperRaw[0];
  lower[0] = lowerRaw[0];

  for (let i = 1; i < len; i++) {
    const close = candles[i].close;
    if (close > upper[i - 1]) direction[i] = DIR_LONG;
    else if (close < lower[i - 1]) direction[i] = DIR_SHORT;
    else direction[i] = direction[i - 1];

    lower[i] = lowerRaw[i];
    if (direction[i] === DIR_LONG && lower[i] < lower[i - 1]) lower[i] = lower[i - 1];

    upper[i] = upperRaw[i];
    if (direction[i] === DIR_SHORT && upper[i] > upper[i - 1]) upper[i] = upper[i - 1];

    const prevDir = direction[i - 1];
    if (direction[i] === DIR_SHORT && prevDir === DIR_LONG) upper[i] = upperRaw[i];
    if (direction[i] === DIR_LONG && prevDir === DIR_SHORT) lower[i] = lowerRaw[i];

    if (direction[i] === DIR_LONG) trendUp[i] = lower[i];
    else trendDown[i] = upper[i];
  }

  return { direction, trendUp, trendDown };
}

// ─── Dashboard interface ────────────────────────────────────────
export interface MaxWealthDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  preset: string;
  entry: number | null;
  stopLoss: number | null;
  trailingStop: number | null;
  target1: number | null;
  target2: number | null;
  target3: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Target3 Hit" | "Stop Loss Hit" | "Trailing SL Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  tp3Hit: boolean;
  slHit: boolean;
  tslHit: boolean;
  candleState: "long" | "short" | "neutral";
}

export const maxWealthIndicator: IndicatorDefinition = {
  id: "max_wealth",
  name: "Max Wealth",
  shortName: "MXW",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "preset", label: "Preset", type: "select", default: "wealth", hidden: true, options: [
      { label: "Wealth", value: "wealth" },
      { label: "Forex", value: "forex" },
    ]},
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "showTrailingStop", label: "Show Moving SL", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#1565C0", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FF0000", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#006400", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#006400", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target3", label: "Target 3", color: "#006400", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const presetKey = params.preset ?? "wealth";
    const displayLevels = params.displayLevels ?? "yes";
    const showTrailingStop = params.showTrailingStop ?? "yes";
    const showBackground = params.showBackground ?? "yes";
    const preset = PRESETS[presetKey] || PRESETS.wealth;
    const len = candles.length;
    const minBars = Math.max(preset.st1Period, preset.st2Period) + 3;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] }, { key: "stopLoss", data: [] },
      { key: "target1", data: [] }, { key: "target2", data: [] }, { key: "target3", data: [] },
    ];

    if (len < minBars) return emptyResult;

    // ════════════════════════════════════════════════════════════
    // 1. Dual SuperTrend
    // ════════════════════════════════════════════════════════════
    const st1 = computeSuperTrend(candles, preset.st1Period, preset.st1Mult);
    const st2 = computeSuperTrend(candles, preset.st2Period, preset.st2Mult);
    const atr = computeATR(candles, preset.st1Period);

    // Combined direction: both must agree
    const combinedDir: Direction[] = new Array(len).fill(DIR_NONE);
    for (let i = 0; i < len; i++) {
      if (st1.direction[i] === DIR_LONG && st2.direction[i] === DIR_LONG) {
        combinedDir[i] = DIR_LONG;
      } else if (st1.direction[i] === DIR_SHORT && st2.direction[i] === DIR_SHORT) {
        combinedDir[i] = DIR_SHORT;
      } else {
        combinedDir[i] = DIR_NONE; // disagreement
      }
    }

    // ════════════════════════════════════════════════════════════
    // 2. Signal Generation
    // ════════════════════════════════════════════════════════════
    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));
    const markers: IndicatorMarker[] = [];
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TSL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    const TP3: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number }[] = [];

    let lastSignalDir: Direction = DIR_NONE;
    let lastSignalBar = -1;
    let currentSignal: Direction = DIR_NONE;
    const lastClosedBar = len - 2;

    for (let i = 1; i <= lastClosedBar; i++) {
      const curCombined = combinedDir[i];

      // Signal fires when both agree AND differs from previous signal
      let newSignal = false;
      if (curCombined === DIR_LONG && currentSignal !== DIR_LONG) {
        newSignal = true;
        currentSignal = DIR_LONG;
      } else if (curCombined === DIR_SHORT && currentSignal !== DIR_SHORT) {
        newSignal = true;
        currentSignal = DIR_SHORT;
      } else if (curCombined === DIR_NONE) {
        // ST disagree — keep current signal tracking but no new signal
      }

      if (newSignal && curCombined !== DIR_NONE) {
        const entry = candles[i].close;
        const eHigh = candles[i].high;
        const eLow = candles[i].low;
        const dir = curCombined === DIR_LONG ? 1.0 : -1.0;

        // MT4 ATR-based risk: distance from entry to SuperTrend band
        const mid = (eHigh + eLow) / 2.0;
        const bandVal = curCombined === DIR_LONG
          ? mid - preset.st1Mult * atr[i]  // lower band for long
          : mid + preset.st1Mult * atr[i]; // upper band for short
        const risk = Math.abs(entry - bandVal);

        SIGNAL[i] = curCombined;
        ENTRY[i] = roundToTick(entry, tick);
        SL[i] = roundToTick(entry - dir * risk * SL_PERCENT, tick);
        TP1[i] = roundToTick(entry + dir * risk * TP1_PERCENT, tick);
        TP2[i] = roundToTick(entry + dir * risk * TP2_PERCENT, tick);
        TP3[i] = roundToTick(entry + dir * risk * TP3_PERCENT, tick);
        TSL[i] = SL[i]; // TSL starts at SL

        lastSignalDir = curCombined;
        lastSignalBar = i;

        if (curCombined === DIR_LONG) {
          markers.push({ time: candles[i].time, position: "belowBar", color: "#008000", shape: "arrowUp", text: "", size: 2 });
          arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low });
        } else {
          markers.push({ time: candles[i].time, position: "aboveBar", color: "#FF0000", shape: "arrowDown", text: "", size: 2 });
          arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high });
        }
      } else {
        SIGNAL[i] = lastSignalDir;
        ENTRY[i] = ENTRY[i - 1] || 0;
        SL[i] = SL[i - 1] || 0;
        TP1[i] = TP1[i - 1] || 0;
        TP2[i] = TP2[i - 1] || 0;
        TP3[i] = TP3[i - 1] || 0;
      }

      // Percentage-based trailing stop (0.35%)
      // Skip the ratchet on the signal bar itself — TSL was already seeded from SL above.
      // Without this guard, the ratchet reads TSL[i-1] from the PREVIOUS trade (wrong direction)
      // and corrupts the new trade's starting TSL value.
      if (!newSignal) {
        if (lastSignalDir === DIR_LONG && lastSignalBar >= 0) {
          const pctTSL = roundToTick(candles[i].close * (100.0 - TSL_PERCENT) / 100.0, tick);
          const prevTSL = TSL[i - 1] > 0 ? TSL[i - 1] : SL[lastSignalBar];
          TSL[i] = Math.max(prevTSL, pctTSL);
        } else if (lastSignalDir === DIR_SHORT && lastSignalBar >= 0) {
          const pctTSL = roundToTick(candles[i].close * (100.0 + TSL_PERCENT) / 100.0, tick);
          const prevTSL = TSL[i - 1] > 0 ? TSL[i - 1] : SL[lastSignalBar];
          TSL[i] = Math.min(prevTSL, pctTSL);
        } else {
          TSL[i] = TSL[i - 1] || 0;
        }
      }

      // Candle colors
      if (lastSignalDir === DIR_LONG) candleColorState[i] = "long";
      else if (lastSignalDir === DIR_SHORT) candleColorState[i] = "short";
      else candleColorState[i] = "neutral";
    }

    // Forward-fill forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar];
        ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar]; TSL[i] = TSL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar]; TP2[i] = TP2[lastClosedBar]; TP3[i] = TP3[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ════════════════════════════════════════════════════════════
    // 3. Target / SL hit detection — chronological bar-by-bar
    //    Priority: targets hit BEFORE stops → book target profit
    // ════════════════════════════════════════════════════════════
    let status: MaxWealthDashboard["status"] = "Active";
    let tp1Hit = false, tp2Hit = false, tp3Hit = false, slHit = false, tslHit = false;
    let bookedPL = 0;

    if (lastSignalBar >= 0) {
      const ev = ENTRY[lastSignalBar], sv = SL[lastSignalBar];
      const t1v = TP1[lastSignalBar], t2v = TP2[lastSignalBar], t3v = TP3[lastSignalBar];
      const sigDir = SIGNAL[lastSignalBar];

      // Find the FIRST bar each level is breached
      let tp1HitBar = -1, tp2HitBar = -1, tp3HitBar = -1, slHitBar = -1, tslHitBar = -1;
      let tslHitPrice = 0;

      // Include live bar (len-1) so status updates in real-time on price touch
      for (let i = lastSignalBar + 1; i < len; i++) {
        if (sigDir === DIR_LONG) {
          if (tp1HitBar < 0 && candles[i].high >= t1v) tp1HitBar = i;
          if (tp2HitBar < 0 && candles[i].high >= t2v) tp2HitBar = i;
          if (tp3HitBar < 0 && candles[i].high >= t3v) tp3HitBar = i;
          if (slHitBar < 0 && candles[i].low <= sv) slHitBar = i;
          if (tslHitBar < 0 && candles[i].low <= TSL[i] && TSL[i] > sv) { tslHitBar = i; tslHitPrice = TSL[i]; }
        } else {
          if (tp1HitBar < 0 && candles[i].low <= t1v) tp1HitBar = i;
          if (tp2HitBar < 0 && candles[i].low <= t2v) tp2HitBar = i;
          if (tp3HitBar < 0 && candles[i].low <= t3v) tp3HitBar = i;
          if (slHitBar < 0 && candles[i].high >= sv) slHitBar = i;
          if (tslHitBar < 0 && candles[i].high >= TSL[i] && TSL[i] < sv) { tslHitBar = i; tslHitPrice = TSL[i]; }
        }
      }

      tp1Hit = tp1HitBar >= 0;
      tp2Hit = tp2HitBar >= 0;
      tp3Hit = tp3HitBar >= 0;
      slHit = slHitBar >= 0;
      tslHit = tslHitBar >= 0;

      const plSign = sigDir === DIR_LONG ? 1 : -1;

      // Priority rules:
      //  • Hard SL hit before a target → SL wins (position closed at a loss, target data is irrelevant)
      //  • Target hit at any bar (even if TSL also fired) → target wins
      //    (TSL is a trailing profit-lock and can trigger on minor pullbacks mid-trend;
      //     if price eventually reached the target the booked result is the target)
      //  • TSL only determines status/P&L when no target was achieved at all
      const hardSLBar = slHit ? slHitBar : Infinity;

      if (tp3Hit && tp3HitBar <= hardSLBar) {
        status = "Target3 Hit"; tp1Hit = true; tp2Hit = true; tp3Hit = true;
        bookedPL = plSign * (t3v - ev);
      } else if (tp2Hit && tp2HitBar <= hardSLBar) {
        status = "Target2 Hit"; tp1Hit = true; tp2Hit = true;
        bookedPL = plSign * (t2v - ev);
      } else if (tp1Hit && tp1HitBar <= hardSLBar) {
        status = "Target1 Hit";
        bookedPL = plSign * (t1v - ev);
      } else if (slHit && (!tslHit || slHitBar <= tslHitBar)) {
        status = "Stop Loss Hit";
        bookedPL = plSign * (sv - ev);
      } else if (tslHit) {
        status = "Trailing SL Hit";
        bookedPL = plSign * (tslHitPrice - ev);
      }
    }

    // ════════════════════════════════════════════════════════════
    // 4. Dashboard metadata
    // ════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const cs = SIGNAL[uiBar], ce = ENTRY[uiBar];

    let dashSignal: MaxWealthDashboard["signal"] = null;
    let dashTrend: MaxWealthDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (cs !== DIR_NONE && ce > 0) {
      if (cs === DIR_LONG) { dashSignal = "BUY"; dashTrend = "LONG"; livePL = currentPrice - ce; }
      else { dashSignal = "SELL"; dashTrend = "SHORT"; livePL = ce - currentPrice; }
    }

    const dashboard: MaxWealthDashboard = {
      signal: dashSignal, trend: dashTrend,
      preset: presetKey === "forex" ? "Forex" : "Wealth",
      entry: ce > 0 ? ce : null,
      stopLoss: SL[uiBar] > 0 ? SL[uiBar] : null,
      trailingStop: TSL[uiBar] > 0 ? TSL[uiBar] : null,
      target1: TP1[uiBar] > 0 ? TP1[uiBar] : null,
      target2: TP2[uiBar] > 0 ? TP2[uiBar] : null,
      target3: TP3[uiBar] > 0 ? TP3[uiBar] : null,
      currentPrice, livePL, bookedPL, status,
      tp1Hit, tp2Hit, tp3Hit, slHit, tslHit,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ════════════════════════════════════════════════════════════
    // 5. Build plot data + candle colors + hit markers
    // ════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];
    const t3Data: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — priceLines handle all level drawing.

    // MT4 candle colors: Green for long, Red for short, WhiteSmoke for neutral
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") candleColors.push({ time: candles[i].time, color: "#008000" });
      else if (state === "short") candleColors.push({ time: candles[i].time, color: "#FF0000" });
      else candleColors.push({ time: candles[i].time, color: "#F5F5F5" });
    }

    // Hit markers
    if (lastSignalBar >= 0 && (tp1Hit || tp2Hit || tp3Hit || slHit)) {
      const sigDir = SIGNAL[lastSignalBar];
      const t1v = TP1[lastSignalBar], t2v = TP2[lastSignalBar], t3v = TP3[lastSignalBar], sv = SL[lastSignalBar];
      let _t1 = tp1Hit, _t2 = tp2Hit, _t3 = tp3Hit, _sl = slHit;
      for (let i = lastSignalBar + 1; i <= Math.min(lastClosedBar, len - 1); i++) {
        if (sigDir === DIR_LONG) {
          if (_t1 && candles[i].high >= t1v) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#006400", text: "✓ T1", size: 1 }); _t1 = false; }
          if (_t2 && candles[i].high >= t2v) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#006400", text: "✓ T2", size: 1 }); _t2 = false; }
          if (_t3 && candles[i].high >= t3v) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#006400", text: "✓ T3", size: 1 }); _t3 = false; }
          if (_sl && candles[i].low <= sv) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#FF0000", text: "✗ SL", size: 1 }); _sl = false; }
        } else {
          if (_t1 && candles[i].low <= t1v) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#006400", text: "✓ T1", size: 1 }); _t1 = false; }
          if (_t2 && candles[i].low <= t2v) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#006400", text: "✓ T2", size: 1 }); _t2 = false; }
          if (_t3 && candles[i].low <= t3v) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#006400", text: "✓ T3", size: 1 }); _t3 = false; }
          if (_sl && candles[i].high >= sv) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#FF0000", text: "✗ SL", size: 1 }); _sl = false; }
        }
      }
    }

    // ── Price lines for full-width horizontal levels with badge labels ──
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && ce > 0 && lastSignalBar >= 0) {
      const entryTime = candles[lastSignalBar].time;
      priceLines.push(
        { id: "mw-entry", price: ce,           color: "#1565C0", title: "Entry",     lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "mw-sl",    price: SL[uiBar],    color: "#C62828", title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "mw-t1",   price: TP1[uiBar],   color: "#2E7D32", title: "Target_1",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "mw-t2",   price: TP2[uiBar],   color: "#2E7D32", title: "Target_2",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "mw-t3",   price: TP3[uiBar],   color: "#2E7D32", title: "Target_3",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
      );
      if (showTrailingStop !== "no" && TSL[uiBar] > 0) {
        priceLines.push(
          { id: "mw-tsl", price: TSL[uiBar], color: "#E65100", title: "Moving SL", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        );
      }
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
      { key: "target3", data: t3Data },
    ];
  },
};
